import { formatDateByDistanceToNow } from "@smart/itops-utils-basic";

import {
  effectiveMaxReminders,
  ReminderScheduleLimits,
} from "../../shared/submission-actions/reminder-helpers";
import { GqlTracker } from "../../types";

export type HistoryEventType =
  | "created"
  | "createdForInternalUse"
  | "sentViaCommunicate"
  | "sentViaEmail"
  | "completed"
  | "savedToMatter"
  | "matterCreated"
  | "opened"
  | "closed"
  | "modified"
  | "submissionLinkCopied"
  | "sentForSignature"
  | "digitallySignedFormReceived"
  | "reminderSentViaCommunicate"
  | "reminderSentViaEmail"
  | "automaticRemindersStopped";

export const historyEventTypeLabel: Record<HistoryEventType, string> = {
  created: "Created",
  createdForInternalUse: "Created",
  sentViaCommunicate: "Shared via Client Portal",
  sentViaEmail: "Shared via Email",
  completed: "Completed",
  savedToMatter: "Responses Saved",
  matterCreated: "Matter Created",
  opened: "Opened",
  closed: "Closed",
  modified: "Modified",
  submissionLinkCopied: "Link Copied",
  sentForSignature: "Sent for signature",
  digitallySignedFormReceived: "Digitally signed form received",
  reminderSentViaCommunicate: "Reminder Sent via Client Portal",
  reminderSentViaEmail: "Reminder Sent via Email",
  automaticRemindersStopped: "Scheduled Reminders Stopped",
};

export const scheduledRemindersSentLabel = (position: number, total: number) =>
  `${position} of ${total} Scheduled Reminders Sent`;

export const scheduledRemindersTotal = ({
  schedule,
  sent,
}: {
  schedule: ReminderScheduleLimits;
  sent: number;
}) =>
  // a lowered max still reads as all sent
  Math.max(effectiveMaxReminders(schedule), sent);

export const getHistoryEventType = (
  tracker: GqlTracker,
  options?: { automaticReminders?: boolean },
): HistoryEventType | undefined => {
  if (options?.automaticReminders) {
    switch (tracker.action) {
      case "reminder-sent-manual":
        if (tracker.method === "email") return "reminderSentViaEmail";
        if (tracker.method === "communicate")
          return "reminderSentViaCommunicate";
        return undefined;
      case "automatic-reminders-stopped":
        return "automaticRemindersStopped";
      default:
        break;
    }
  }

  switch (tracker.action) {
    case "saved-to-matter":
      return "savedToMatter";
    case "matter-created":
      return "matterCreated";
    case "submission-created":
      if (tracker.method === "internalUse") return "createdForInternalUse";
      return "created";
    case "submission-modified":
      return "modified";
    case "submission-closed":
      return "closed";
    case "submission-completed":
      return "completed";
    case "intake-form-opened":
      return "opened";
    case "submission-link-copied":
      return "submissionLinkCopied";
    case "sent-for-signature":
      return "sentForSignature";
    case "digitally-signed-form-received":
      return "digitallySignedFormReceived";
    case "reminder-sent-manual":
    case "submission-sent":
    case null: // for backward compatibility only
      if (tracker.method === "email") {
        return "sentViaEmail";
      }
      if (tracker.method === "communicate") {
        return "sentViaCommunicate";
      }
      return undefined;
    default:
      return undefined;
  }
};

export const formatEventDate = (
  date: string | null | undefined,
  options?: { preposition?: string },
) =>
  date
    ? formatDateByDistanceToNow(new Date(date), {
        daysLimit: 7,
        preposition: options?.preposition,
      })
    : undefined;
